/**
 * Navigation model for the owner console. Paths mirror the Flutter routes
 * (`lib/app/routes.dart` + `router.dart`): `/owner/**` for the cloud module, and the shared
 * staff-ops screens (attendance, expenses) which the owner opens in their cloud variant.
 *
 * `group` decides where a destination appears: `primary` in the bottom / side nav,
 * `more` in the More sheet and the rest of the side nav, `hidden` only as a route.
 */
import { lazy, LazyExoticComponent, ComponentType } from 'react';
import { IconType } from 'react-icons';
import {
    LuBadgeIndianRupee,
    LuBedDouble,
    LuCalendarClock,
    LuChartPie,
    LuClock,
    LuCreditCard,
    LuFileText,
    LuHandCoins,
    LuIndianRupee,
    LuLayers,
    LuLayoutDashboard,
    LuListChecks,
    LuPercent,
    LuReceipt,
    LuSettings2,
    LuStore,
    LuTable2,
    LuTags,
    LuTimer,
    LuTrash2,
    LuTrendingUp,
    LuUpload,
    LuUserCheck,
    LuUsers,
    LuUtensils,
} from 'react-icons/lu';
import { Tr } from './i18n/l10n';
import { ownerStrings } from './i18n/owner-strings';
import { staffOpsStrings } from './i18n/staff-ops-strings';
import { webStrings } from './i18n/web-strings';

const Dashboard = lazy(() => import('./pages/owner/dashboard'));
const Menu = lazy(() => import('./pages/owner/menu'));
const Categories = lazy(() => import('./pages/owner/categories'));
const Pricing = lazy(() => import('./pages/owner/pricing'));
const Slots = lazy(() => import('./pages/owner/slots'));
const UsersDevices = lazy(() => import('./pages/owner/users-devices'));
const Reservations = lazy(() => import('./pages/owner/reservations'));
const Reports = lazy(() => import('./pages/owner/reports'));
const Salary = lazy(() => import('./pages/owner/salary'));
const ExpenseTypes = lazy(() => import('./pages/owner/expense-types'));
const Attendance = lazy(() => import('./pages/owner/attendance'));
const Expenses = lazy(() => import('./pages/owner/expenses'));
const OutletProfile = lazy(() => import('./pages/owner/outlet'));
const HubUpload = lazy(() => import('./pages/owner/hub-upload'));
const ExpenseNew = lazy(() => import('./pages/owner/expense-new'));

const SalesReport = lazy(() => import('./pages/owner/reports/sales'));
const ItemsReport = lazy(() => import('./pages/owner/reports/items'));
const ProfitReport = lazy(() => import('./pages/owner/reports/profit'));
const TaxReport = lazy(() => import('./pages/owner/reports/tax'));
const KotReport = lazy(() => import('./pages/owner/reports/kot'));
const TablesReport = lazy(() => import('./pages/owner/reports/tables'));
const StaffReport = lazy(() => import('./pages/owner/reports/staff'));
const WasteReport = lazy(() => import('./pages/owner/reports/waste'));
const PaymentsReport = lazy(() => import('./pages/owner/reports/payments'));
const RoomChargeReport = lazy(() => import('./pages/owner/reports/room-charges'));

export type RouteGroup = 'primary' | 'more' | 'hidden';

/** A label in the current language: `route.name(tr)`. */
export type Label = (tr: Tr) => string;

const own = (key: keyof typeof ownerStrings.en): Label => (tr) => tr(ownerStrings, key);
const web = (key: keyof typeof webStrings.en): Label => (tr) => tr(webStrings, key);

export interface AppRoute {
    /** Path under `/owner`, as registered in the shell's <Routes>. */
    path: string;
    /** Full path for links. */
    to: string;
    name: Label;
    /** One word for the mobile bottom nav, where the full name does not fit. */
    short?: Label;
    icon: IconType;
    group: RouteGroup;
    element: LazyExoticComponent<ComponentType<any>>;
}

const route = (
    path: string,
    name: Label,
    icon: IconType,
    group: RouteGroup,
    element: LazyExoticComponent<ComponentType<any>>,
    short?: Label,
): AppRoute => ({ path, to: `/owner/${path}`, name, short, icon, group, element });

export const appRoutes: AppRoute[] = [
    route('dashboard', web('navDashboard'), LuLayoutDashboard, 'primary', Dashboard),
    route('reports', own('lReports'), LuChartPie, 'primary', Reports),
    route('menu', own('lMenu'), LuUtensils, 'primary', Menu, web('navMenuShort')),
    route('reservations', own('lReserve'), LuCalendarClock, 'primary', Reservations),

    route('categories', own('lCategories'), LuTags, 'more', Categories),
    route('pricing', own('lPricing'), LuBadgeIndianRupee, 'more', Pricing),
    route('slots', own('lSlots'), LuClock, 'more', Slots),
    route('users', own('lUsers'), LuUsers, 'more', UsersDevices),
    route('salary', own('lSalary'), LuHandCoins, 'more', Salary),
    route('attendance', own('lAttendance'), LuUserCheck, 'more', Attendance),
    route('expenses', own('lExpenses'), LuReceipt, 'more', Expenses),
    route('expense-types', own('lExpenseTypes'), LuLayers, 'more', ExpenseTypes),
    route('hub-upload', web('navHubUpload'), LuUpload, 'more', HubUpload),
    route('outlet', web('navOutlet'), LuSettings2, 'more', OutletProfile),

    route('expenses/new', (tr) => tr(staffOpsStrings, 'logExpense'), LuReceipt, 'hidden', ExpenseNew),
    route('reports/sales', own('rpSales'), LuTrendingUp, 'hidden', SalesReport),
    route('reports/items', own('rpItems'), LuListChecks, 'hidden', ItemsReport),
    route('reports/profit', own('rpProfit'), LuIndianRupee, 'hidden', ProfitReport),
    route('reports/tax', web('navTax'), LuPercent, 'hidden', TaxReport),
    route('reports/kot', own('rpKot'), LuTimer, 'hidden', KotReport),
    route('reports/tables', own('rpTables'), LuTable2, 'hidden', TablesReport),
    route('reports/staff', own('rpStaff'), LuStore, 'hidden', StaffReport),
    route('reports/waste', own('rpWaste'), LuTrash2, 'hidden', WasteReport),
    route('reports/payments', own('rpPay'), LuCreditCard, 'hidden', PaymentsReport),
    route('reports/room-charges', own('rpRoom'), LuBedDouble, 'hidden', RoomChargeReport),
];

export const primaryRoutes = appRoutes.filter((r) => r.group === 'primary');
export const moreRoutes = appRoutes.filter((r) => r.group === 'more');

/** The 10 detailed reports, in the order the report picker lists them. */
export const reportRoutes: { to: string; name: Label; sub: Label; icon: IconType }[] = [
    { to: '/owner/reports/sales', name: own('rpSales'), sub: web('subSales'), icon: LuTrendingUp },
    { to: '/owner/reports/items', name: own('rpItems'), sub: web('subItems'), icon: LuListChecks },
    { to: '/owner/reports/profit', name: own('rpProfit'), sub: web('subProfit'), icon: LuIndianRupee },
    { to: '/owner/reports/tax', name: own('rpTax'), sub: own('gstr1'), icon: LuPercent },
    { to: '/owner/reports/kot', name: own('rpKot'), sub: web('subKot'), icon: LuTimer },
    { to: '/owner/reports/tables', name: own('rpTables'), sub: web('subTables'), icon: LuTable2 },
    { to: '/owner/reports/staff', name: own('rpStaff'), sub: web('subStaff'), icon: LuStore },
    { to: '/owner/reports/waste', name: own('rpWaste'), sub: web('subWaste'), icon: LuTrash2 },
    { to: '/owner/reports/payments', name: own('rpPay'), sub: web('subPay'), icon: LuCreditCard },
    { to: '/owner/reports/room-charges', name: own('rpRoom'), sub: web('subRoom'), icon: LuBedDouble },
];

/** Quick links on the dashboard (the Flutter `_LinkGrid`). */
export const dashboardLinks: { to: string; name: Label; icon: IconType }[] = [
    { to: '/owner/menu', name: own('lMenu'), icon: LuUtensils },
    { to: '/owner/categories', name: own('lCategories'), icon: LuTags },
    { to: '/owner/pricing', name: own('lPricing'), icon: LuBadgeIndianRupee },
    { to: '/owner/slots', name: own('lSlots'), icon: LuClock },
    { to: '/owner/users', name: own('lUsers'), icon: LuUsers },
    { to: '/owner/reports', name: own('lReports'), icon: LuChartPie },
    { to: '/owner/reservations', name: own('lReserve'), icon: LuCalendarClock },
    { to: '/owner/salary', name: own('lSalary'), icon: LuHandCoins },
    { to: '/owner/expenses', name: own('lExpenses'), icon: LuReceipt },
    { to: '/owner/expense-types', name: own('lExpenseTypes'), icon: LuLayers },
    { to: '/owner/attendance', name: own('lAttendance'), icon: LuUserCheck },
    { to: '/owner/reports/profit', name: own('rpProfit'), icon: LuFileText },
];
